import React, { useEffect, useState } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { get, post } from '../../utils/api';
import { GET_CANDIDATE_PROFILE, UPDATE_PROFILE, UPDATE_PROFILE_SUMMARY, UPDATE_CAREER_PROFILE, ADD_SKILLS, GET_DEPARTMENTS, GET_CATEGORIES } from '../../config/api';
import { noticePeriodOptionsFor } from '../../constants/noticePeriods';
import { titleCaseOnBlur, titleCaseOnChange } from '../../utils/nameCase';

const EMPLOYMENT_TYPES = ['full-time', 'part-time', 'contract'];

const SkillChips = ({ values, onChange }) => {
    const [text, setText] = useState('');

    const addChip = (raw) => {
        const value = raw.trim();
        if (!value || values.includes(value)) return;
        onChange([...values, value]);
        setText('');
    };

    return (
        <div>
            <div className="flex flex-wrap gap-2 mb-2">
                {values.map((v) => (
                    <span key={v} className="flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-800">
                        {v}
                        <button type="button" onClick={() => onChange(values.filter((s) => s !== v))} aria-label={`Remove ${v}`}>
                            <X size={13} className="text-gray-400 hover:text-gray-700" />
                        </button>
                    </span>
                ))}
            </div>
            <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChip(text); } }}
                placeholder="Add a skill and press Enter"
                className="w-full border-b border-gray-300 pb-1 text-sm focus:outline-none focus:border-talexo-blue"
            />
        </div>
    );
};

// In-chat "Complete your profile" form (same pattern as PreferencesEditorPanel):
// everything on one screen, saved via the real profile endpoints directly —
// no LLM round-trips, no one-question-at-a-time chat. Mirrors the real
// profile page's sections: basic info + designation (EditProfileHeaderModal),
// Resume Headline, skills, and career preferences.
// Loads current values first and sends them all back on save because
// updateCandidateProfile/updateCareerProfile overwrite their full field sets
// (see KNOWLEDGE.md §2) — same "merge before write" reasoning as the chatbot's
// applyProfileUpdate.js for single-field chat edits.
const ProfileEditorPanel = ({ onClose, onSaved }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Basic info (updateCandidateProfile)
    const [name, setName] = useState('');
    const [location, setLocation] = useState('');
    const [experience, setExperience] = useState('');
    const [currentSalary, setCurrentSalary] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [currentCompany, setCurrentCompany] = useState('');

    // Resume headline (updateProfileSummary)
    const [profileSummary, setProfileSummary] = useState('');

    // Subject — the sub_categories level of Department -> Category ->
    // sub_categories. Labelled "Subject" because this is the jobseeker's own
    // profile and they are picking what they teach; the job-posting forms and
    // the browse page keep calling the same field "Designation", since under
    // the Office and Management departments its values are roles, not
    // subjects. See EditProfileHeaderModal for the full reasoning.
    // Same taxonomy as that modal, rather than free text that might not match
    // what recruiters search against.
    const [departments, setDepartments] = useState([]);
    const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
    const [department, setDepartment] = useState('');
    const [categories, setCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    // Several subjects, stored comma-separated in currentDesignation. Cap kept
    // in step with MAX_SUBJECTS in utils/categoryDesignationMatcher.js.
    const MAX_SUBJECTS = 5;
    /*
     * "Other" — kept in step with EditProfileHeaderModal.jsx, which offers the
     * same option. Not a sub_categories row: the profile stores subject NAMES,
     * and held as its own flag because it belongs to no category and so cannot
     * be recovered from the taxonomy on reload.
     */
    const OTHER_SUBJECT = 'Other';
    const [otherSelected, setOtherSelected] = useState(false);
    const [subCategoryIds, setSubCategoryIds] = useState([]);
    const [subjectSearch, setSubjectSearch] = useState('');
    const [currentDesignation, setCurrentDesignation] = useState('');

    // Career preferences (updateCareerProfile)
    const [noticePeriod, setNoticePeriod] = useState('');
    const [employmentType, setEmploymentType] = useState('');
    const [expectedSalary, setExpectedSalary] = useState('');
    const [skills, setSkills] = useState([]);
    const [otherCareerFields, setOtherCareerFields] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const res = await get(GET_CANDIDATE_PROFILE);
                const data = res?.responseData || {};
                setName(data.name || '');
                setLocation(data.currentLocation || '');
                setExperience(data.totalExperience || '');
                setCurrentSalary(data.currentSalary || '');
                setPhone(data.phone || '');
                setEmail(data.email || '');
                setCurrentCompany(data.currentCompany || '');
                setProfileSummary(data.profileSummary || '');
                setDepartment(data.department || '');
                setCurrentDesignation(data.currentDesignation || '');
                setNoticePeriod(data.noticePeriod || '');
                setEmploymentType(data.employmentType || '');
                setExpectedSalary(data.expectedSalary || '');
                setSkills(data.skills || []);
                setOtherCareerFields({
                    preferredLocations: data.preferredLocations,
                    preferredRoles: data.preferredRoles,
                    desiredJobType: data.desiredJobType,
                });
            } catch {
                // Leave fields empty — user can still fill and save fresh.
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Load departments once, then preselect the one matching the profile's
    // existing free-text department once both are available.
    useEffect(() => {
        (async () => {
            try {
                const res = await post(GET_DEPARTMENTS, {});
                if (res?.responseCode === 200) setDepartments(res.responseData || []);
            } catch {
                // Designation dropdown just stays empty — free-text fields still save fine.
            }
        })();
    }, []);

    useEffect(() => {
        if (!departments.length || !department || selectedDepartmentId) return;
        const dept = departments.find((d) => d.name === department);
        if (dept) setSelectedDepartmentId(String(dept.id));
    }, [departments, department, selectedDepartmentId]);

    useEffect(() => {
        if (!selectedDepartmentId) { setCategories([]); return; }
        (async () => {
            try {
                const res = await post(GET_CATEGORIES, { departmentId: selectedDepartmentId });
                if (res?.responseCode === 200) setCategories(res.responseData || []);
            } catch {
                setCategories([]);
            }
        })();
    }, [selectedDepartmentId]);

    // Preselect the category and every subject already on the profile, once
    // categories have loaded. The stored value is comma-separated; parts that
    // do not match a sub-category (legacy free text) are simply not ticked.
    useEffect(() => {
        if (!currentDesignation || !categories.length || subCategoryIds.length) return;
        const wanted = String(currentDesignation)
            .split(',').map((part) => part.trim().toLowerCase()).filter(Boolean);
        if (!wanted.length) return;
        // Outside the loop: "Other" matches no category, so it must not depend
        // on one being found.
        setOtherSelected(wanted.includes(OTHER_SUBJECT.toLowerCase()));
        for (const category of categories) {
            const hits = (category.sub_categories || []).filter(
                (sub) => wanted.includes(sub.name?.toLowerCase()),
            );
            if (hits.length) {
                setSelectedCategoryId(String(category.id));
                setSubCategoryIds(hits.slice(0, MAX_SUBJECTS).map((sub) => String(sub.id)));
                break;
            }
        }
    }, [currentDesignation, categories, subCategoryIds.length]);

    const subCategories = categories.find((c) => String(c.id) === selectedCategoryId)?.sub_categories || [];
    /*
     * Only the SEARCH is filtered, never the selection. An already-ticked
     * subject that the search text excludes stays selected and still saves —
     * filtering a list must not silently change what it holds.
     */
    const visibleSubCategories = subjectSearch.trim()
        ? subCategories.filter((sub) => (sub.name || '').toLowerCase().includes(subjectSearch.trim().toLowerCase()))
        : subCategories;

    const handleDepartmentChange = (e) => {
        const departmentId = e.target.value;
        setSelectedDepartmentId(departmentId);
        const dept = departments.find((d) => String(d.id) === departmentId);
        setDepartment(dept?.name || '');
        setSelectedCategoryId('');
        setSubCategoryIds([]);
        setOtherSelected(false);
        setSubjectSearch('');
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Dropdown order, not tick order, so the saved string is stable.
            const picked = subCategories
                .filter((sub) => subCategoryIds.includes(String(sub.id)))
                .map((sub) => sub.name)
                .filter(Boolean);
            // Falls back to whatever was already on the profile when the taxonomy
            // could not be loaded and the free-text box was used instead.
            if (otherSelected) picked.push(OTHER_SUBJECT);
            const finalDesignation = picked.length ? picked.join(', ') : (currentDesignation || '');

            await post(UPDATE_PROFILE, {
                name: name || null,
                location: location || null,
                experience: experience ? Number(experience) : null,
                salary: currentSalary ? Number(currentSalary) : null,
                phone: phone || null,
                email: email || null,
                currentCompany: currentCompany || null,
                currentDesignation: finalDesignation || null,
                department: department || null,
            });

            if (profileSummary) {
                await post(UPDATE_PROFILE_SUMMARY, { profileSummary });
            }

            await post(UPDATE_CAREER_PROFILE, {
                ...otherCareerFields,
                currentDesignation: finalDesignation || null,
                department: department || null,
                noticePeriod: noticePeriod || null,
                employmentType: employmentType || null,
                expectedSalary: expectedSalary ? Number(expectedSalary) : null,
            });

            await post(ADD_SKILLS, { skills });
            onSaved?.();
        } catch {
            // Best-effort — the chat will reflect current values next time regardless.
        } finally {
            setSaving(false);
        }
    };

    const labelCls = 'text-sm font-medium text-gray-700 mb-2 block';
    const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-talexo-blue';
    const selectCls = `${inputCls} bg-white`;

    return (
        <div className="flex h-full w-full flex-col bg-white">
            <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
                <button type="button" onClick={onClose} aria-label="Back">
                    <ArrowLeft size={20} className="text-gray-600" />
                </button>
                <span className="font-semibold text-gray-800">Complete your profile</span>
            </div>

            {loading ? (
                <div className="flex-1 flex items-center justify-center text-sm text-gray-400">Loading…</div>
            ) : (
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
                    <p className="text-sm text-gray-500">A complete profile gets you better job matches and more recruiter attention.</p>

                    <div>
                        <label className={labelCls}>Name</label>
                        <input type="text" value={name} onChange={titleCaseOnChange(setName)}
                            onBlur={titleCaseOnBlur(setName)}
                            placeholder="Your full name" className={inputCls} />
                    </div>

                    <div>
                        <label className={labelCls}>Profile headline</label>
                        <textarea value={profileSummary} onChange={(e) => setProfileSummary(e.target.value)} placeholder="A one-line summary recruiters see first" rows={2} className={inputCls} />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Department</label>
                            <select value={selectedDepartmentId} onChange={handleDepartmentChange} className={selectCls}>
                                <option value="">Select…</option>
                                {departments.map((d) => (
                                    <option key={d.id} value={d.id}>{d.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={labelCls}>Category</label>
                            <select value={selectedCategoryId} onChange={(e) => { setSelectedCategoryId(e.target.value); setSubCategoryIds([]); setSubjectSearch(''); }} disabled={!selectedDepartmentId} className={selectCls}>
                                <option value="">Select…</option>
                                {categories.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className={labelCls}>Subjects</label>
                        {/* A checkbox list rather than <select multiple>: this panel is
                            narrow, and a native multi-select there needs ctrl-click to
                            add a second option, which almost nobody discovers. The search
                            box sits OUTSIDE the scrolling list so it stays put while the
                            list scrolls under it. */}
                        {selectedCategoryId && subCategories.length > 0 && (
                            <input
                                type="text"
                                value={subjectSearch}
                                onChange={(e) => setSubjectSearch(e.target.value)}
                                placeholder="Search subjects…"
                                aria-label="Search subjects"
                                className={`${inputCls} mb-1`}
                            />
                        )}
                        <div className={`max-h-40 overflow-y-auto rounded-md border border-gray-300 p-1 ${!selectedCategoryId ? 'opacity-50' : ''}`}>
                            {!selectedCategoryId ? (
                                <p className="px-2 py-1.5 text-xs text-gray-400">Choose a role category first</p>
                            ) : subCategories.length === 0 ? (
                                <p className="px-2 py-1.5 text-xs text-gray-400">No subjects for this category</p>
                            ) : visibleSubCategories.length === 0 ? (
                                <p className="px-2 py-1.5 text-xs text-gray-400">No subject matches that</p>
                            ) : (
                                visibleSubCategories.map((sub) => {
                                    const id = String(sub.id);
                                    const checked = subCategoryIds.includes(id);
                                    // Other counts towards the cap, so the ceiling is the
                                    // same however it is reached.
                                    const chosenCount = subCategoryIds.length + (otherSelected ? 1 : 0);
                                    const atCap = !checked && chosenCount >= MAX_SUBJECTS;
                                    return (
                                        <label
                                            key={sub.id}
                                            className={`flex items-center gap-2 rounded px-2 py-1 text-sm ${atCap ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer hover:bg-gray-50'}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                disabled={atCap}
                                                onChange={() => setSubCategoryIds((prev) => (
                                                    checked ? prev.filter((v) => v !== id) : [...prev, id]
                                                ))}
                                                className="h-4 w-4 rounded border-gray-300 text-talexo-blue focus:ring-talexo-blue/40"
                                            />
                                            <span>{sub.name}</span>
                                        </label>
                                    );
                                })
                            )}
                            {selectedCategoryId
                                && OTHER_SUBJECT.toLowerCase().includes(subjectSearch.trim().toLowerCase())
                                && (() => {
                                    const chosenCount = subCategoryIds.length + (otherSelected ? 1 : 0);
                                    const atCap = !otherSelected && chosenCount >= MAX_SUBJECTS;
                                    return (
                                        <label
                                            className={`mt-1 flex items-center gap-2 rounded border-t border-gray-100 px-2 py-1 pt-2 text-sm ${atCap ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer hover:bg-gray-50'}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={otherSelected}
                                                disabled={atCap}
                                                onChange={() => setOtherSelected((v) => !v)}
                                                className="h-4 w-4 rounded border-gray-300 text-talexo-blue focus:ring-talexo-blue/40"
                                            />
                                            <span>{OTHER_SUBJECT}</span>
                                            <span className="text-xs text-gray-400">— my subject is not listed</span>
                                        </label>
                                    );
                                })()}
                        </div>
                        {selectedCategoryId && (
                            <p className="mt-1 text-xs text-gray-500">
                                {subCategoryIds.length + (otherSelected ? 1 : 0)}/{MAX_SUBJECTS} chosen — you will be matched to jobs for any of them
                            </p>
                        )}
                        {!departments.length && (
                            <input type="text" value={currentDesignation} onChange={(e) => setCurrentDesignation(e.target.value)} placeholder="e.g. PGT Mathematics Teacher" className={`${inputCls} mt-2`} />
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Current location</label>
                            <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Delhi" className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>Total experience (yrs)</label>
                            <input type="number" value={experience} onChange={(e) => setExperience(e.target.value)} className={inputCls} />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Phone</label>
                            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile" className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>Email</label>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Current institution</label>
                            <input type="text" value={currentCompany} onChange={(e) => setCurrentCompany(e.target.value)} className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>Current annual salary</label>
                            <div className="flex items-center gap-2 border-b border-gray-300 pb-1">
                                <span className="text-gray-500">₹</span>
                                <input type="number" value={currentSalary} onChange={(e) => setCurrentSalary(e.target.value)} className="flex-1 text-sm focus:outline-none" placeholder="0" />
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className={labelCls}>Skills</label>
                        <SkillChips values={skills} onChange={setSkills} />
                    </div>

                    <div>
                        <label className={labelCls}>Notice period</label>
                        <div className="flex flex-wrap gap-2">
                            {noticePeriodOptionsFor(noticePeriod).map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => setNoticePeriod(option.value)}
                                    className={`rounded-full border px-3 py-1.5 text-sm ${noticePeriod === option.value ? 'border-talexo-blue bg-talexo-blue/10 text-talexo-blue-dark font-medium' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className={labelCls}>Employment type</label>
                        <div className="flex flex-wrap gap-2">
                            {EMPLOYMENT_TYPES.map((et) => (
                                <button
                                    key={et}
                                    type="button"
                                    onClick={() => setEmploymentType(et)}
                                    className={`rounded-full border px-3 py-1.5 text-sm capitalize ${employmentType === et ? 'border-talexo-blue bg-talexo-blue/10 text-talexo-blue-dark font-medium' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                >
                                    {et}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className={labelCls}>Expected annual salary</label>
                        <div className="flex items-center gap-2 border-b border-gray-300 pb-1">
                            <span className="text-gray-500">₹</span>
                            <input type="number" value={expectedSalary} onChange={(e) => setExpectedSalary(e.target.value)} className="flex-1 text-sm focus:outline-none" placeholder="0" />
                        </div>
                    </div>
                </div>
            )}

            <div className="flex justify-end gap-2 border-t border-gray-100 p-3">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || loading}
                    className="rounded-full bg-talexo-blue px-5 py-2 text-sm font-medium text-white hover:bg-talexo-blue-dark disabled:opacity-50"
                >
                    {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    disabled={saving}
                    className="rounded-full border border-talexo-blue/40 bg-white px-5 py-2 text-sm font-medium text-talexo-blue-dark hover:bg-talexo-blue/10 disabled:opacity-50"
                >
                    Go back
                </button>
            </div>
        </div>
    );
};

export default ProfileEditorPanel;
